'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

interface SnackbarMessage {
  severity: SnackbarSeverity;
  message: string;
}

interface SnackbarContextValue {
  showSnackbar: (params: SnackbarMessage) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export const useSnackbar = (): SnackbarContextValue => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<SnackbarMessage | null>(null);

  const showSnackbar = useCallback((params: SnackbarMessage) => {
    setCurrent(params);
    setOpen(true);
  }, []);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={current?.severity ?? 'info'} variant="filled">
          {current?.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
